import React from "react";
import {
    Boxes,
    Activity,
    Receipt,
    ArrowUpRight,
    Check,
    ShieldCheck,
    Cloud,
    BarChart3,
} from "lucide-react";

const MODULES = [
    {
        icon: Boxes,
        title: "Drug Inventory",
        tagline: "Batch · Expiry · Multi-store",
        points: [
            "Batch-wise stock with expiry-first dispensing",
            "Auto reorder at min/max thresholds",
            "GS1 barcode & 2D code scanning",
            "Multi-location & chain-pharmacy rollup",
        ],
    },
    {
        icon: Activity,
        title: "Statistical Process Control",
        tagline: "QC · Deviations · CAPA",
        points: [
            "X-bar, R, p, np, c, u charts out-of-the-box",
            "Western Electric rule violations & alerts",
            "Cpk / Ppk capability tracking",
            "Deviation, CAPA & audit-trail logs",
        ],
    },
    {
        icon: Receipt,
        title: "Billing & GST",
        tagline: "Invoice · Claims · e-Invoice",
        points: [
            "GST-compliant invoicing & e-invoice IRN",
            "Insurance & TPA claim workflow",
            "Counter billing with scan-to-bill",
            "Credit notes, returns & audit ready",
        ],
    },
];

function Sparkline() {
    // Small inline SVG trend (simulated dashboard widget)
    const pts = [6, 10, 8, 14, 11, 18, 16, 22, 20, 26, 23, 29];
    const max = Math.max(...pts);
    const path = pts
        .map(
            (p, i) =>
                `${(i / (pts.length - 1)) * 100},${40 - (p / max) * 36}`
        )
        .join(" ");
    return (
        <svg viewBox="0 0 100 40" className="w-full h-10" preserveAspectRatio="none">
            <polyline
                points={path}
                fill="none"
                stroke="#4D7C0F"
                strokeWidth="1.4"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <polyline
                points={`${path} 100,40 0,40`}
                fill="#4D7C0F"
                opacity="0.08"
            />
        </svg>
    );
}

function SpcChart() {
    // Stylised control chart
    const vals = [14, 17, 15, 19, 13, 20, 15, 22, 17, 25, 19, 21];
    const UCL = 28,
        LCL = 8,
        CL = 18;
    return (
        <svg viewBox="0 0 200 80" className="w-full h-20">
            <line x1="0" y1={80 - (UCL / 35) * 80} x2="200" y2={80 - (UCL / 35) * 80} stroke="#DC2626" strokeDasharray="2 2" strokeWidth="0.6" />
            <line x1="0" y1={80 - (CL / 35) * 80} x2="200" y2={80 - (CL / 35) * 80} stroke="#0C4A6E" strokeWidth="0.8" />
            <line x1="0" y1={80 - (LCL / 35) * 80} x2="200" y2={80 - (LCL / 35) * 80} stroke="#DC2626" strokeDasharray="2 2" strokeWidth="0.6" />
            {vals.map((v, i) => {
                const x = (i / (vals.length - 1)) * 200;
                const y = 80 - (v / 35) * 80;
                return (
                    <g key={i}>
                        {i > 0 && (
                            <line
                                x1={((i - 1) / (vals.length - 1)) * 200}
                                y1={80 - (vals[i - 1] / 35) * 80}
                                x2={x}
                                y2={y}
                                stroke="#0C4A6E"
                                strokeWidth="0.8"
                            />
                        )}
                        <circle cx={x} cy={y} r="2" fill="#0C4A6E" />
                    </g>
                );
            })}
            <text x="2" y={80 - (UCL / 35) * 80 - 2} fontSize="5" fill="#DC2626" fontFamily="monospace">UCL</text>
            <text x="2" y={80 - (CL / 35) * 80 - 2} fontSize="5" fill="#0C4A6E" fontFamily="monospace">CL</text>
            <text x="2" y={80 - (LCL / 35) * 80 - 2} fontSize="5" fill="#DC2626" fontFamily="monospace">LCL</text>
        </svg>
    );
}

export default function RxSaaS() {
    return (
        <section
            id="saas"
            className="bg-ink text-white py-24 md:py-32 relative overflow-hidden"
            data-testid="saas-section"
        >
            {/* decorative grid */}
            <div
                className="absolute inset-0 opacity-[0.06] pointer-events-none"
                style={{
                    backgroundImage:
                        "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
                    backgroundSize: "40px 40px",
                }}
            />

            <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
                <div className="grid md:grid-cols-12 gap-10 items-end mb-14 md:mb-16">
                    <div className="md:col-span-7">
                        <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#BAE6FD] mb-5">
                            <span className="inline-block w-8 h-px bg-white/40 align-middle mr-3" />
                            Momia PharmaOps · Software as a Service
                        </div>
                        <h2 className="font-cabinet font-bold text-4xl md:text-5xl lg:text-[56px] leading-[1.02] tracking-tight">
                            Pharmacy inventory,
                            <br />
                            <span className="italic font-normal text-[#BAE6FD]">
                                SPC quality & billing —
                            </span>
                            <br />
                            in a single cloud workspace.
                        </h2>
                    </div>
                    <div className="md:col-span-5">
                        <p className="text-base md:text-lg text-white/80 leading-relaxed">
                            Built for pharmacy chains, hospital pharmacies and
                            manufacturing QC teams. One login for real-time
                            stock, statistical process control and
                            regulator-ready billing. No servers, no installs —
                            just sign in on any browser.
                        </p>
                    </div>
                </div>

                {/* Dashboard mockup */}
                <div
                    className="grid md:grid-cols-12 gap-4 md:gap-5 mb-14"
                    data-testid="saas-dashboard-mockup"
                >
                    <div className="md:col-span-4 bg-white text-ink p-6 relative">
                        <div className="flex items-center justify-between mb-2">
                            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-stone-500">
                                Inventory Value
                            </div>
                            <Boxes size={16} strokeWidth={1.5} className="text-[#0C4A6E]" />
                        </div>
                        <div className="font-cabinet font-black text-4xl tracking-tight">
                            ₹ 42.8L
                        </div>
                        <div className="mt-2 flex items-center gap-2 text-xs text-[#4D7C0F] font-mono">
                            ▲ 6.2% vs last week
                        </div>
                        <Sparkline />
                    </div>

                    <div className="md:col-span-5 bg-white text-ink p-6">
                        <div className="flex items-center justify-between mb-2">
                            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-stone-500">
                                SPC · Fill Weight · X-bar
                            </div>
                            <Activity size={16} strokeWidth={1.5} className="text-[#0C4A6E]" />
                        </div>
                        <SpcChart />
                        <div className="mt-2 flex items-center gap-4 text-[10px] font-mono text-stone-500 uppercase tracking-[0.15em]">
                            <span>Cpk 1.42</span>
                            <span>σ 0.31</span>
                            <span className="text-[#4D7C0F]">In control</span>
                        </div>
                    </div>

                    <div className="md:col-span-3 bg-white text-ink p-6">
                        <div className="flex items-center justify-between mb-2">
                            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-stone-500">
                                Today's Billing
                            </div>
                            <Receipt size={16} strokeWidth={1.5} className="text-[#0C4A6E]" />
                        </div>
                        <div className="font-cabinet font-black text-4xl tracking-tight">
                            148
                        </div>
                        <div className="text-xs text-stone-500 mt-1">invoices · ₹ 3.1L</div>
                        <div className="mt-4 h-1.5 w-full bg-stone-100">
                            <div className="h-full bg-[#0C4A6E]" style={{ width: "72%" }} />
                        </div>
                        <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.18em] text-stone-500">
                            72% of daily target
                        </div>
                    </div>
                </div>

                {/* Module cards */}
                <div className="grid md:grid-cols-3 gap-4 md:gap-5">
                    {MODULES.map((m, i) => (
                        <div
                            key={m.title}
                            className="bg-white/[0.04] border border-white/10 p-7 md:p-8 backdrop-blur-sm hover:bg-white/[0.06] hover:border-[#BAE6FD]/30 transition-all"
                            data-testid={`saas-module-${i}`}
                        >
                            <div className="flex items-center justify-between mb-6">
                                <m.icon
                                    size={22}
                                    strokeWidth={1.5}
                                    className="text-[#BAE6FD]"
                                />
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">
                                    Module 0{i + 1}
                                </span>
                            </div>
                            <h3 className="font-cabinet font-bold text-2xl leading-tight tracking-tight">
                                {m.title}
                            </h3>
                            <div className="mt-1 font-mono text-[11px] uppercase tracking-[0.18em] text-[#BAE6FD]/80">
                                {m.tagline}
                            </div>
                            <ul className="mt-5 space-y-2">
                                {m.points.map((p) => (
                                    <li
                                        key={p}
                                        className="flex gap-3 text-sm text-white/85"
                                    >
                                        <Check
                                            size={14}
                                            strokeWidth={2}
                                            className="text-[#4D7C0F] shrink-0 mt-1"
                                        />
                                        <span>{p}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>

                {/* Trust strip + CTA */}
                <div className="mt-14 grid md:grid-cols-12 gap-6 items-center">
                    <div className="md:col-span-7 flex flex-wrap gap-x-8 gap-y-3 text-sm text-white/70">
                        <div className="flex items-center gap-2">
                            <Cloud size={16} strokeWidth={1.5} className="text-[#BAE6FD]" />
                            Cloud · browser-based · zero install
                        </div>
                        <div className="flex items-center gap-2">
                            <ShieldCheck size={16} strokeWidth={1.5} className="text-[#BAE6FD]" />
                            GST · e-Invoice · 21 CFR Part 11 ready
                        </div>
                        <div className="flex items-center gap-2">
                            <BarChart3 size={16} strokeWidth={1.5} className="text-[#BAE6FD]" />
                            SPC charts & CAPA built-in
                        </div>
                    </div>
                    <div className="md:col-span-5 flex md:justify-end">
                        <a
                            href="#inquiry"
                            onClick={(e) => {
                                e.preventDefault();
                                // Try to prefill category if the select exists
                                const sel = document.querySelector(
                                    '[data-testid="rfq-category"]'
                                );
                                if (sel) {
                                    const opt = [...sel.options].find(
                                        (o) =>
                                            o.value ===
                                            "Momia PharmaOps (SaaS)"
                                    );
                                    if (opt) {
                                        sel.value = opt.value;
                                        sel.dispatchEvent(
                                            new Event("change", {
                                                bubbles: true,
                                            })
                                        );
                                    }
                                }
                                document
                                    .getElementById("inquiry")
                                    ?.scrollIntoView({
                                        behavior: "smooth",
                                        block: "start",
                                    });
                            }}
                            className="inline-flex items-center gap-3 bg-[#BAE6FD] text-ink px-7 py-4 hover:bg-white transition-colors font-medium"
                            data-testid="saas-demo-cta"
                        >
                            Request a Demo
                            <ArrowUpRight size={18} strokeWidth={1.5} />
                        </a>
                    </div>
                </div>
            </div>
        </section>
    );
}
